import Box from "@mui/material/Box";
import FormLabel from "@mui/material/FormLabel";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Field } from "formik";
import { useEffect, useRef, useState } from "react";

export default function Input({
	id = null,
	name,
	label,
	sx,
	auto_complete = null,
	min = null,
	max = null,
	multi_line = false,
	rows = 2,
	step = 0.01,
	type = "text",
	placeholder = "",
	disabled = false,
	auto_focus = false,
	helper = null,
	variant = "outlined",
	size = "medium",
	show_large_label = false,
	required = false,
	only_positive = false,
	on_change = null,
	full_width = true,
	start_adornment = null,
	end_adornment = null,
	show_error_on_exit = false,
	input_props = null,
}) {
	const inputRef = useRef(null);
	const [touched, setTouched] = useState(false);

	useEffect(() => {
		if (auto_focus && inputRef.current) {
			inputRef.current.focus();
		}
	}, [auto_focus]);

	const handleBlur = () => {
		if (show_error_on_exit) {
			setTouched(true);
		}
	};

	return (
		<Field name={name}>
			{({ field, meta }) => {
				// PREVENT ERRORS WHEN DATA NOT LOADED YET
				if (field.value === null || field.value === undefined) {
					field.value = "";
				}

				// SET MIN AND MAX VALUE FOR INPUT (ONLY WHEN NUMBER)
				if (type === "number") {
					const numValue = Number.parseFloat(field.value);
					if (!Number.isNaN(numValue)) {
						if (min !== null) {
							field.value = Math.max(min, numValue);
						}
						if (max !== null) {
							field.value = Math.min(max, numValue);
						}
					}
				}

				const showError = meta.error && (show_error_on_exit ? touched : meta.touched);

				return (
					<>
						{show_large_label && (
							<FormLabel error={showError}>
								<Typography sx={{ fontSize: 14, fontWeight: 500, m: 0, mb: 0.5, p: 0 }} noWrap>
									{label}{" "}
									{required && (
										<Box component="span" sx={{ color: "error.main", fontSize: 14 }}>
											*
										</Box>
									)}
								</Typography>
							</FormLabel>
						)}
						<TextField
							{...field}
							rows={multi_line ? rows : 1}
							multiline={multi_line}
							error={showError}
							helperText={showError ? meta.error : helper}
							id={id}
							label={show_large_label ? null : `${label}${required ? " *" : ""}`}
							name={name}
							autoComplete={auto_complete}
							disabled={disabled}
							onChange={(event) => {
								field.onChange(event);
								on_change?.(event);
							}}
							type={type}
							variant={variant}
							size={size}
							sx={{
								...sx,
								width: full_width ? "100%" : "auto",
							}}
							placeholder={placeholder}
							onBlur={(event) => {
								handleBlur();
								field.onBlur(event);
							}}
							onFocus={() => setTouched(false)}
							InputProps={{
								endAdornment: end_adornment && (
									<InputAdornment position="end">{end_adornment}</InputAdornment>
								),
								inputProps: {
									min: only_positive ? 0 : null,
									step: { step: step },
								},
								inputRef: inputRef, // Ref for autofocus
								startAdornment: start_adornment && (
									<InputAdornment position="start">{start_adornment}</InputAdornment>
								),
								...input_props,
							}}
						/>
					</>
				);
			}}
		</Field>
	);
}
